import React, { useEffect, useState } from 'react';
import { useRawImputationDimensions } from 'routes/BudgetModule/config/hooks';
import Loading from 'components/Loading';
import NotFound from 'components/Errors/NotFound';
import { useGetStatusesSettings } from 'routes/BudgetModule/helpers/hooks/statusesSettings';
import { useGetIntegrations } from 'helpers/integrations';
import CallToActionBudget from 'components/ModuleAccess/CallToActions/CallToActionBudget';
import SecondaryBudgetAxisSourceTableProvider from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableProvider';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';
import { BudgetModuleContext } from 'routes/BudgetModule/components/BudgetWithProvider/BudgetModuleContext';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';

type Props = {
    children?: React.ReactNode;
};

const BudgetWithProvider = ({
    children = <></>,
}: Props): React.ReactElement => {
    // We display the Budget CTA if any of these conditions:
    // - The bind does not have the module: this is dealt with in a generic manner in BudgetWithCTA
    // - The bind does not have the required integrations for budget to work properly:
    //   this is dealt with in this component with this state.
    const [shouldDisplayCTA, setShouldDisplayCTA] = useState(false);

    const {
        isFetching: isImputationDimensionsFetching,
        isError: isImputationDimensionsError,
        error: imputationDimensionsError,
        imputationDimensions: imputationRawDimensions,
    } = useRawImputationDimensions();

    // TODO SMELL: quick fix due to the removal of onError on react-query v5.
    useEffect(() => {
        if (
            imputationDimensionsError &&
            imputationDimensionsError.status === 404
        ) {
            // TODO In BudgetWithProvider test, we are expecting an console.error
            // with the error. I couldn't find in the original code where this
            // console.error was, but since the move to react-query v5, it does show
            // in the test. My guess is that it was handled by the dispatch notifyUser
            // before ? And now that we use the global handler via `meta: {useGlobalOnError:true}`
            // it does not show up in test.
            // This is not critical, and to ensure parity with the existing code, I added the
            // console.error here.
            // But one day we should probably either fix the test (by handling the global error
            // handler ? mocking it ?) or remove check on console.error if it is not needed.
            console.error(imputationDimensionsError);
            setShouldDisplayCTA(true);
        }
    }, [imputationDimensionsError, setShouldDisplayCTA]);

    const {
        data: statusesSettings = {},
        isFetching: isStatusesSettingsLoading,
        isError: isStatusesSettingsError,
    } = useGetStatusesSettings();

    const {
        data: integrations,
        isFetching: isIntegrationsLoading,
        isError: isIntegrationsError,
    } = useGetIntegrations();

    if (
        isImputationDimensionsFetching ||
        isStatusesSettingsLoading ||
        isIntegrationsLoading
    ) {
        return (
            <div className="page-container">
                <Loading />
            </div>
        );
    }

    if (
        isImputationDimensionsError ||
        isStatusesSettingsError ||
        isIntegrationsError
    ) {
        return shouldDisplayCTA ? <CallToActionBudget /> : <NotFound />;
    }

    return (
        <BudgetModuleContext.Provider value={true}>
            <IntegrationContext.Provider value={integrations!}>
                <ImputationDimensionsContext.Provider
                    value={imputationRawDimensions}
                >
                    <StatusSettingsContext.Provider value={statusesSettings}>
                        <SecondaryBudgetAxisSourceTableProvider>
                            {children}
                        </SecondaryBudgetAxisSourceTableProvider>
                    </StatusSettingsContext.Provider>
                </ImputationDimensionsContext.Provider>
            </IntegrationContext.Provider>
        </BudgetModuleContext.Provider>
    );
};

export default BudgetWithProvider;
